import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import BadgeStar from '../../components/shared/BadgeStar'
import NotificationBell from '../../components/shared/NotificationBell'
import RollBOT from '../../components/shared/RollBOT'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts'

const getRPEColor = (rpe) => {
  if (!rpe) return '#334155'
  if (rpe >= 9) return '#ef4444'
  if (rpe >= 7) return '#fb923c'
  if (rpe >= 5) return '#facc15'
  return '#34d399'
}

const getRPELabel = (rpe) => {
  if (!rpe) return ''
  if (rpe >= 9) return 'Sangat Berat'
  if (rpe >= 7) return 'Berat'
  if (rpe >= 5) return 'Sedang'
  return 'Ringan'
}

const dayTypeLabel = {
  mtb_xc: 'MTB/XC', road_passing: 'Road Passing',
  indoor: 'Indoor', hill_repeat: 'Hill Repeat', long_ride: 'Long Ride'
}

export default function ParentDashboard({ profile }) {
  const [loading, setLoading] = useState(true)
  const [child, setChild] = useState(null)
  const [childProfile, setChildProfile] = useState(null)
  const [logs, setLogs] = useState([])
  const [badges, setBadges] = useState([])
  const [coachNotes, setCoachNotes] = useState([])
  const [trendData, setTrendData] = useState([])
  const [clubAvg, setClubAvg] = useState(null)
  const [hillLogs, setHillLogs] = useState([])
  const [teamAverages, setTeamAverages] = useState(null)
  const [activeTab, setActiveTab] = useState('ringkasan')
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchChildData()
  }, [profile.id])

  const fetchChildData = async () => {
    setLoading(true)
    setError(null)

    try {
      // 1. Cari athlete yang parent_id = profile.id
      const { data: athleteData, error: athleteError } = await supabase
  .from('athletes')
  .select('*')
  .eq('parent_id', profile.id)
  .single()

if (athleteError || !athleteData) {
  setError('Belum ada atlet yang terhubung ke akun ini. Hubungi admin.')
  setLoading(false)
  return
}

setChild(athleteData)
      // 2. Ambil session logs
      const { data: logData } = await supabase
        .from('session_logs')
        .select('*, sessions(session_date, day_type, mode)')
        .eq('athlete_id', athleteData.id)
        .order('created_at', { ascending: false })
        .limit(20)
      setLogs(logData || [])
      // 2b. Trend 5 sesi terakhir
      const { data: childProfileData } = await supabase
  .from('profiles')
  .select('id, full_name, role')
  .eq('id', athleteData.profile_id)
  .single()

setChildProfile(childProfileData)

      // 3. Ambil badges
      const { data: badgeData } = await supabase
        .from('athlete_badges')
        .select('*, badges(*)')
        .eq('athlete_id', athleteData.id)
      setBadges(badgeData || [])

      // 4. Ambil catatan coach
      const { data: noteData } = await supabase
        .from('coach_notes')
        .select('*, profiles!coach_notes_coach_id_fkey(full_name)')
        .eq('athlete_id', athleteData.id)
        .order('created_at', { ascending: false })
        .limit(5)
      setCoachNotes(noteData || [])

      // 5. Hill repeat logs
      if (logData && logData.length > 0) {
        const { data: hillData } = await supabase
          .from('hill_repeat_logs')
          .select('*')
          .in('session_log_id', logData.map(l => l.id))
        setHillLogs(hillData || [])
      }

      // 6. Rata-rata kategori (teamAverages untuk RollBOT)
      if (athleteData?.category) {
        const { data: teamData } = await supabase
          .from('session_logs')
          .select('avg_speed, cadence, heart_rate, athletes!inner(category)')
          .eq('athletes.category', athleteData.category)
          .limit(100)
        if (teamData && teamData.length > 0) {
          const validSpeed   = teamData.map(d => d.avg_speed).filter(Boolean)
          const validCadence = teamData.map(d => d.cadence).filter(Boolean)
          const validHr      = teamData.map(d => d.heart_rate).filter(Boolean)
          setTeamAverages({
            avg_speed: validSpeed.length   ? (validSpeed.reduce((a,b)=>a+b,0)   / validSpeed.length).toFixed(1)   : null,
            cadence:   validCadence.length ? (validCadence.reduce((a,b)=>a+b,0) / validCadence.length).toFixed(1) : null,
            hr:        validHr.length      ? (validHr.reduce((a,b)=>a+b,0)      / validHr.length).toFixed(1)      : null,
          })
        }
      }

    } catch (err) {
      setError(err.message)
    }

    setLoading(false)
  }

  // Kehadiran 7 hari terakhir
  const weekDays = ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min']
  const today = new Date()
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today)
    d.setDate(today.getDate() - (6 - i))
    return d.toISOString().split('T')[0]
  })

  const weeklyAttendance = last7Days.map((date, i) => {
    const log = logs.find(l => l.sessions?.session_date === date)
    return {
      day: weekDays[new Date(date).getDay() === 0 ? 6 : new Date(date).getDay() - 1],
      hadir: !!log,
      rpe: log?.rpe || null
    }
  })

  const attendanceRate = Math.round(
    (weeklyAttendance.filter(d => d.hadir).length / 7) * 100
  )

  const streak = (() => {
    const dates = [...new Set(logs.map(l => l.sessions?.session_date).filter(Boolean))].sort().reverse()
    let count = 0
    let prev = null
    for (const d of dates) {
      if (!prev) { count = 1; prev = d; continue }
      const diff = (new Date(prev) - new Date(d)) / 86400000
      if (diff <= 7) { count++; prev = d } else break
    }
    return count
  })()

  if (loading) return (
    <div style={{
      minHeight: '100vh', background: '#0f172a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#a78bfa', fontSize: '1rem'
    }}>
      Loading...
    </div>
  )

  if (error) return (
    <div style={{
      minHeight: '100vh', background: '#0f172a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: '1rem', padding: '2rem'
    }}>
      <div style={{ fontSize: '3rem' }}>⚠️</div>
      <div style={{ color: '#f87171', textAlign: 'center' }}>{error}</div>
      <button
        onClick={() => supabase.auth.signOut()}
        style={{
          padding: '0.5rem 1.5rem', background: '#334155',
          border: 'none', borderRadius: '8px',
          color: '#94a3b8', cursor: 'pointer'
        }}
      >
        Keluar
      </button>
    </div>
  )

  const lastLog = logs[0]

  const CompBar = ({ label, atletVal, clubVal, unit, higherIsBetter = true }) => {
  if (!atletVal || !clubVal) return null
  const diff = atletVal - clubVal
  const pct = Math.abs((diff / clubVal) * 100).toFixed(0)
  const isAbove = diff >= 0
  const isGood = higherIsBetter ? isAbove : !isAbove
  const color = isGood ? '#34d399' : '#f87171'
  const barWidth = Math.min((atletVal / (clubVal * 1.4)) * 100, 100)
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{label}</span>
        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#f1f5f9' }}>
          {atletVal} {unit}
          <span style={{ color, marginLeft: '6px', fontSize: '0.65rem' }}>
            {isAbove ? '▲' : '▼'} {pct}% vs klub
          </span>
        </span>
      </div>
      <div style={{ height: '6px', background: '#0f172a', borderRadius: '999px', overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: '999px',
          background: color, width: `${barWidth}%`,
          transition: 'width 0.6s ease'
        }} />
      </div>
      <div style={{ fontSize: '0.6rem', color: '#475569', marginTop: '2px' }}>
        Rata-rata klub: {clubVal} {unit}
      </div>
    </div>
  )
}
  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#f1f5f9' }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #1e293b',
        background: '#0a0f1e'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <img src="/logo_rollaco.png" alt="Rollaco"
            style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
          <div>
            <div style={{ color: '#a78bfa', fontWeight: 'bold', fontSize: '1rem' }}>
              ROLLACO
            </div>
            <div style={{ color: '#94a3b8', fontSize: '0.65rem', letterSpacing: '0.1em' }}>
              LAPORAN ORANG TUA
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#f1f5f9', fontSize: '0.9rem', fontWeight: 'bold' }}>
              {profile?.full_name}
            </div>
            <div style={{ color: '#a78bfa', fontSize: '0.7rem', textTransform: 'uppercase' }}>
              Orang Tua
            </div>
          </div>
          {/* ── Notification Bell ── */}
          {profile?.id && <NotificationBell userId={profile.id} />}
          <button
            onClick={() => supabase.auth.signOut()}
            style={{
              background: '#1e293b', border: '1px solid #334155',
              borderRadius: '8px', color: '#94a3b8',
              padding: '0.4rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem'
            }}
          >
            Keluar
          </button>
        </div>
      </div>

      <div style={{ padding: '1rem 1.5rem' }}>

        {/* ── Tab Bar ── */}
        <div style={{
          display: 'flex', gap: '0.5rem',
          marginBottom: '1rem',
          background: '#1e293b',
          borderRadius: '12px',
          padding: '4px',
        }}>
          {[
            { id: 'ringkasan', label: '📊 Ringkasan' },
            { id: 'rollbot',   label: '✦ RollBOT' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                flex: 1,
                padding: '0.5rem',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                transition: 'all 0.2s',
                background: activeTab === t.id ? '#a78bfa' : 'transparent',
                color:      activeTab === t.id ? '#0f172a'  : '#64748b',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Tab: RollBOT ── */}
        {activeTab === 'rollbot' && (
          <div style={{ paddingTop: 4 }}>
            <RollBOT
              athlete={child}
              sessions={logs}
              coachNotes={coachNotes}
              hillLogs={hillLogs}
              badges={badges}
              teamAverages={teamAverages}
              role="parent"
            />
          </div>
        )}

        {/* ── Tab: Ringkasan ── */}
        {activeTab === 'ringkasan' && (<>

        {/* Card anak */}
        <div style={{
          background: '#1e293b',
          border: '2px solid #a78bfa44',
          borderRadius: '16px', padding: '1.25rem',
          marginBottom: '1rem',
          display: 'flex', alignItems: 'center', gap: '1rem'
        }}>
          <div style={{
            width: '3.5rem', height: '3.5rem', borderRadius: '50%',
            background: '#0f172a', border: '2px solid #a78bfa',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '2rem', flexShrink: 0
          }}>
            🚴
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
              {childProfile?.full_name}
            </div>
            <div style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
              {child?.category || 'Kategori belum diset'}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.4rem' }}>
              <span style={{ fontSize: '0.7rem', color: '#fb923c' }}>
                🔥 Streak <strong>{streak} sesi</strong>
              </span>
              <span style={{ fontSize: '0.7rem', color: '#38bdf8' }}>
                📊 <strong>{logs.length}</strong> sesi tercatat
              </span>
              <span style={{ fontSize: '0.7rem', color: '#facc15' }}>
                🏆 <strong>{badges.length}</strong> badge
              </span>
            </div>
          </div>
        </div>

        {/* Sesi terakhir */}
        {lastLog && (
          <div style={{
            background: `${getRPEColor(lastLog.rpe)}15`,
            border: `2px solid ${getRPEColor(lastLog.rpe)}44`,
            borderRadius: '12px', padding: '1rem',
            marginBottom: '1rem'
          }}>
        {/* Trend 5 sesi terakhir */}
{trendData.length > 1 && (
  <div style={{
    background: '#1e293b', borderRadius: '12px',
    padding: '1rem', marginBottom: '1rem'
  }}>
    <div style={{ color: '#f1f5f9', fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
      📈 Trend 5 Sesi Terakhir
    </div>
    <div style={{ fontSize: '0.65rem', color: '#475569', marginBottom: '0.75rem' }}>
      RPE · Speed (km/j) · HR (bpm)
    </div>
    <ResponsiveContainer width="100%" height={180}>
      <LineChart data={trendData} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
        <XAxis dataKey="sesi" tick={{ fill: '#64748b', fontSize: 10 }} />
        <YAxis tick={{ fill: '#64748b', fontSize: 10 }} />
        <Tooltip
          contentStyle={{
            background: '#0f172a', border: '1px solid #334155',
            borderRadius: '8px', fontSize: '0.7rem'
          }}
          labelStyle={{ color: '#94a3b8' }}
        />
        <Legend wrapperStyle={{ fontSize: '0.65rem', color: '#94a3b8' }} />
        <Line type="monotone" dataKey="rpe"   name="RPE"   stroke="#fb923c" strokeWidth={2} dot={{ r: 3 }} connectNulls />
        <Line type="monotone" dataKey="speed" name="Speed" stroke="#facc15" strokeWidth={2} dot={{ r: 3 }} connectNulls />
        <Line type="monotone" dataKey="hr"    name="HR"    stroke="#f87171" strokeWidth={2} dot={{ r: 3 }} connectNulls />
      </LineChart>
    </ResponsiveContainer>
  </div>
)}

{/* Vs rata-rata klub */}
{clubAvg && lastLog && (
  <div style={{
    background: '#1e293b', borderRadius: '12px',
    padding: '1rem', marginBottom: '1rem'
  }}>
    <div style={{ color: '#f1f5f9', fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
      ⚖️ Vs Rata-rata Klub
    </div>
    <div style={{ fontSize: '0.65rem', color: '#475569', marginBottom: '0.75rem' }}>
      Berdasarkan sesi terakhir · Hijau = lebih baik
    </div>
    <CompBar label="Avg Speed" atletVal={lastLog.avg_speed ? parseFloat(lastLog.avg_speed.toFixed(1)) : null}
      clubVal={clubAvg.speed} unit="km/j" higherIsBetter={true} />
    <CompBar label="Heart Rate" atletVal={lastLog.heart_rate ? parseFloat(lastLog.heart_rate.toFixed(0)) : null}
      clubVal={clubAvg.hr} unit="bpm" higherIsBetter={false} />
    <CompBar label="Cadence" atletVal={lastLog.cadence ? parseFloat(lastLog.cadence.toFixed(0)) : null}
      clubVal={clubAvg.cadence} unit="rpm" higherIsBetter={true} />
  </div>
)}
            <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
              🕐 Sesi Terakhir
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    {dayTypeLabel[lastLog.sessions?.day_type] || '-'}
                    {lastLog.sessions?.mode ? ` — ${lastLog.sessions.mode}` : ''}
                  </span>
                </div>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                  {lastLog.sessions?.session_date || '-'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  fontSize: '1.5rem', fontWeight: 'bold',
                  color: getRPEColor(lastLog.rpe)
                }}>
                  RPE {lastLog.rpe}
                </div>
                <div style={{ fontSize: '0.65rem', color: getRPEColor(lastLog.rpe) }}>
                  {getRPELabel(lastLog.rpe)}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem' }}>
              {[
                { label: 'Cadence', value: lastLog.cadence ? `${lastLog.cadence} rpm` : '-', color: '#f472b6' },
                { label: 'Speed', value: lastLog.avg_speed ? `${lastLog.avg_speed} km/j` : '-', color: '#facc15' },
                { label: 'Jarak', value: lastLog.distance ? `${lastLog.distance} km` : '-', color: '#4ade80' },
                { label: 'HR', value: lastLog.heart_rate ? `${lastLog.heart_rate} bpm` : '-', color: '#f87171' },
              ].map(m => (
                <div key={m.label} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{m.label}</div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: m.color }}>{m.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Kehadiran minggu ini */}
        <div style={{
          background: '#1e293b', borderRadius: '12px',
          padding: '1rem', marginBottom: '1rem'
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', marginBottom: '0.75rem'
          }}>
            <div style={{ color: '#f1f5f9', fontWeight: 'bold', fontSize: '0.85rem' }}>
              📅 Kehadiran 7 Hari Terakhir
            </div>
            <div style={{
              fontSize: '0.75rem', fontWeight: 'bold',
              color: attendanceRate >= 70 ? '#34d399' : '#f87171'
            }}>
              {attendanceRate}%
            </div>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            {weeklyAttendance.map((d, i) => (
              <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{
                  height: '32px',
                  background: d.hadir ? `${getRPEColor(d.rpe)}33` : '#1e293b',
                  border: d.hadir ? `2px solid ${getRPEColor(d.rpe)}` : '2px solid #334155',
                  borderRadius: '6px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', fontWeight: 'bold',
                  color: d.hadir ? getRPEColor(d.rpe) : '#475569'
                }}>
                  {d.hadir ? d.rpe : '—'}
                </div>
                <div style={{ fontSize: '0.5rem', color: '#94a3b8', marginTop: '2px' }}>
                  {d.day}
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: '0.65rem', color: '#475569', marginTop: '0.5rem', textAlign: 'center' }}>
            Angka = RPE · Kotak gelap = tidak ada sesi tercatat
          </div>
        </div>

        {/* Badge */}
        {badges.length > 0 && (
          <div style={{
            background: '#1e293b', borderRadius: '12px',
            padding: '1rem', marginBottom: '1rem'
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: '0.75rem'
            }}>
              <div style={{ color: '#facc15', fontWeight: 'bold', fontSize: '0.85rem' }}>
                🏆 Badge Diraih
              </div>
              <div style={{ color: '#94a3b8', fontSize: '0.7rem' }}>
                {badges.length} badge
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {badges.map(ab => (
                <div key={ab.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  background: `${ab.badges?.color || '#facc15'}18`,
                  border: `1px solid ${ab.badges?.color || '#facc15'}44`,
                  borderRadius: '8px', padding: '0.4rem 0.6rem'
                }}>
                  <span style={{ fontSize: '1rem' }}>{ab.badges?.icon || '⭐'}</span>
                  <div>
                    <div style={{
                      fontSize: '0.7rem', fontWeight: 'bold',
                      color: ab.badges?.color || '#facc15'
                    }}>
                      {ab.badges?.name}
                    </div>
                    <div style={{ fontSize: '0.55rem', color: '#94a3b8' }}>
                      {ab.earned_at?.slice(0, 10)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div style={{ marginTop: '0.75rem' }}>
              <div style={{ height: '6px', background: '#0f172a', borderRadius: '999px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${(badges.length / 27) * 100}%`,
                  background: 'linear-gradient(to right, #facc15, #fb923c)',
                  borderRadius: '999px'
                }} />
              </div>
              <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '0.25rem', textAlign: 'right' }}>
                {badges.length} dari 27 badge diraih
              </div>
            </div>
          </div>
        )}

        {/* Catatan coach */}
        {coachNotes.length > 0 && (
          <div style={{
            background: '#34d39915',
            border: '1px solid #34d39944',
            borderRadius: '12px', padding: '1rem',
            marginBottom: '1rem'
          }}>
            <div style={{ color: '#34d399', fontWeight: 'bold', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              📝 Catatan Coach
            </div>
            {coachNotes.map(note => (
              <div key={note.id} style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '0.8rem', color: '#cbd5e1', lineHeight: 1.6 }}>
                  {note.note}
                </div>
                <div style={{ fontSize: '0.65rem', color: '#475569', marginTop: '0.25rem' }}>
                  {note.profiles?.full_name} • {note.created_at?.slice(0, 10)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Riwayat sesi */}
        {logs.length > 0 && (
          <div style={{
            background: '#1e293b', borderRadius: '12px',
            padding: '1rem', marginBottom: '1rem'
          }}>
            <div style={{ color: '#f1f5f9', fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              📋 Riwayat Sesi
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {logs.slice(0, 10).map(log => (
                <div key={log.id} style={{
                  background: '#0f172a', borderRadius: '8px',
                  padding: '0.5rem 0.75rem',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                      {dayTypeLabel[log.sessions?.day_type] || '-'}
                      {log.sessions?.mode ? ` — ${log.sessions.mode}` : ''}
                    </div>
                    <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                      {log.sessions?.session_date} •
                      Speed: {log.avg_speed || '-'} •
                      HR: {log.heart_rate || '-'}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '0.9rem', fontWeight: 'bold',
                    color: getRPEColor(log.rpe)
                  }}>
                    RPE {log.rpe}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Label read only */}
        <div style={{
          textAlign: 'center', color: '#475569',
          fontSize: '0.7rem', padding: '0.5rem'
        }}>
          🔒 Tampilan hanya baca · Untuk pertanyaan, hubungi coach langsung
        </div>
        </>)}

      </div>
    </div>
  )
}